import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import Tooltip from 'components/layout/Tooltip/Tooltip';
import { getRegionalClockDisplay } from 'utils/date';
import { STAT_PERIOD_OPTIONS } from 'constants/dashboard';
import { selectStatPeriod, setStatPeriod } from 'store/slices/dashboardSlice';

function DashboardToolbar({ user = {} }) {
  const dispatch = useDispatch();
  const { settings } = user;
  const statPeriod = useSelector(selectStatPeriod);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { label, dateTimeAttr, preferenceLabels } = useMemo(
    () => getRegionalClockDisplay(settings ?? {}, now),
    [settings, now]
  );

  const tooltipContent = (
    <ul className="space-y-0.5">
      <li>Start of the week: <span className="text-gray-100">{preferenceLabels.startOfWeek}</span></li>
      <li>Date format: <span className="text-gray-100">{preferenceLabels.dateFormat}</span></li>
      <li>Time format: <span className="text-gray-100">{preferenceLabels.timeFormat}</span></li>
    </ul>
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-sm">
        <Tooltip content={tooltipContent}>
          <time dateTime={dateTimeAttr} className="tabular-nums text-gray-300 cursor-default">
            {label}
          </time>
        </Tooltip>
        <Link
          to="/settings"
          aria-label="Open Settings to change regional preferences"
          className={classNames(
            'inline-flex shrink-0 rounded-md p-1 text-gray-400',
            'hover:bg-gray-800 hover:text-indigo-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900'
          )}
        >
          <Cog6ToothIcon className="h-5 w-5" aria-hidden />
        </Link>
      </div>
      <div className="flex items-center gap-1 rounded-lg bg-gray-800/50 p-1">
        {STAT_PERIOD_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => dispatch(setStatPeriod(option.value))}
            className={classNames(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer',
              statPeriod === option.value
                ? 'bg-indigo-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default DashboardToolbar;
